"use client"

import { useState, useEffect, useRef } from "react"
import { Bell, BookOpen, FileText, MessageSquare, School, AlertCircle, PenTool, X, Trash2 } from "lucide-react"
import { getAllNotification } from "./api"
import { postAws } from "../aws"

const iconMap = {
  assignment: FileText,
  quiz: PenTool,
  library: BookOpen,
  announcement: MessageSquare,
  classroom: School,
  general: AlertCircle,
}

const iconColors = {
  assignment: "text-blue-600",
  quiz: "text-green-600",
  library: "text-amber-600",
  announcement: "text-purple-600",
  classroom: "text-red-600",
  general: "text-gray-600",
}

const NotificationBell = ({ iconOnly }) => {
  const [isMobile, setIsMobile] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const bellRef = useRef(null)

  // Check if mobile view
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768)
    }

    // Initial check
    checkIfMobile()

    // Add event listener for window resize
    window.addEventListener("resize", checkIfMobile)

    // Cleanup
    return () => window.removeEventListener("resize", checkIfMobile)
  }, [])

  const unseenCount = notifications.filter((n) => n.seen === "N").length

  useEffect(() => {
    const getNotifications = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const fetchedNotifications = await getAllNotification(localStorage.getItem("studentId"))
        setNotifications(fetchedNotifications.body)
      } catch (err) {
        setError("Failed to fetch notifications")
        console.error("Error fetching notifications:", err)
      } finally {
        setIsLoading(false)
      }
    }

    getNotifications()
  }, [])

  const toggleNotifications = async () => {
    setIsOpen(!isOpen)
    if (!isOpen) {
      setNotifications(notifications.map((n) => ({ ...n, seen: "Y" })))
      try {
        await postAws("https://api-dev.aurait.ai/alert/seen", "POST", {
          id: localStorage.getItem("studentId"),
        })
      } catch (error) {
        console.error("Error marking notifications as seen:", error)
      }
    }
  }

  const closeNotifications = () => {
    setIsOpen(false)
  }

  const deleteNotification = async (id, event) => {
    event.stopPropagation()
    try {
      await postAws("https://api-dev.aurait.ai/alert/delete", "POST", { id })
      setNotifications(notifications.filter((n) => n._id !== id))
    } catch (error) {
      console.error("Error deleting notification:", error)
    }
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (bellRef.current && !bellRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  const formatNotificationTime = (timestamp) => {
    const now = new Date()
    const notificationTime = new Date(timestamp)
    const diffInSeconds = Math.floor((now - notificationTime) / 1000)

    if (diffInSeconds < 60) {
      return "Just now"
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60)
      return `${minutes}m ago`
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600)
      return `${hours}h ago`
    } else {
      return notificationTime.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })
    }
  }

  return (
    <div className={`relative inline-block ${isMobile ? "-mt-2" : "mt-2"}`} ref={bellRef}>
      <button
        onClick={toggleNotifications}
        className="p-2 text-gray-500 hover:text-gray-700 focus:outline-none"
        style={{ WebkitTapHighlightColor: "transparent" }}
        aria-label="Toggle notifications"
      >
        <Bell
          className={`${iconOnly ? "w-5 h-5" : "w-6 h-6"} ${unseenCount > 0 ? "text-indigo-500" : "text-gray-600"}`}
        />
        {unseenCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-5 w-5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75 animate-ping"></span>
            <span className="relative inline-flex rounded-full h-5 w-5 bg-indigo-500 text-white text-xs font-medium items-center justify-center">
              {unseenCount > 9 ? "9+" : unseenCount}
            </span>
          </span>
        )}
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-50 md:absolute md:inset-auto md:right-0 md:top-full md:mt-1 md:w-96">
          {/* Mobile overlay background */}
          <div className="fixed inset-0 bg-black/20 backdrop-blur-sm md:hidden" onClick={closeNotifications} />

          {/* Notification panel */}
          <div className="fixed inset-x-0 bottom-0 md:bottom-auto w-full md:w-80 bg-white rounded-t-2xl md:rounded-xl shadow-2xl md:absolute md:right-0 animate-slide-up md:animate-fade-in">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-semibold text-gray-900">Notifications</h3>
                {unseenCount > 0 && (
                  <span className="bg-indigo-100 text-indigo-600 text-xs font-medium px-2 py-0.5 rounded-full">
                    {unseenCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <button onClick={closeNotifications} className="p-2 text-gray-400 hover:text-gray-600 rounded-full">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Notification list */}
            <div className="overflow-y-auto max-h-[80vh] md:max-h-[60vh]">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="w-16 h-16 rounded-full bg-indigo-50 flex items-center justify-center mb-4">
                    <Bell className="w-8 h-8 text-indigo-400 animate-pulse" />
                  </div>
                  <p className="text-sm text-gray-500">Loading notifications...</p>
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
                    <AlertCircle className="w-8 h-8 text-red-400" />
                  </div>
                  <p className="text-base font-medium text-gray-900 mb-1">Error</p>
                  <p className="text-sm text-gray-500 text-center">{error}</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <div className="w-16 h-16 rounded-full bg-indigo-50 flex items-center justify-center mb-4">
                    <Bell className="w-8 h-8 text-indigo-400" />
                  </div>
                  <p className="text-base font-medium text-gray-900 mb-1">No notifications</p>
                  <p className="text-sm text-gray-500 text-center">
                    You're all caught up! New notifications will appear here.
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {notifications.map((notification) => {
                    const IconComponent = iconMap[notification.refType] || AlertCircle
                    const iconColorClass = iconColors[notification.refType] || "text-gray-600"
                    const isUnread = notification.seen === "N"

                    return (
                      <div
                        key={notification._id}
                        className={`px-4 py-3 hover:bg-gray-50 active:bg-gray-100 cursor-pointer transition-colors ${
                          isUnread ? "bg-indigo-50/50" : ""
                        }`}
                      >
                        <div className="flex gap-3">
                          <IconComponent className={`w-5 h-5 mt-0.5 flex-shrink-0 ${iconColorClass}`} />
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm ${isUnread ? "font-medium text-gray-900" : "text-gray-800"}`}>
                              {notification.alertMessage}
                            </p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-500">
                                {notification.classValue}-{notification.subject}
                              </span>
                              <span className="text-xs text-gray-400">•</span>
                              <span className="text-xs text-gray-500">
                                {formatNotificationTime(notification.createdAt)}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={(e) => deleteNotification(notification._id, e)}
                            className="p-2 text-gray-400 hover:text-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            {/* Mobile close button */}
            <div className="md:hidden p-4 border-t">
              <button
                onClick={closeNotifications}
                className="w-full py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default NotificationBell

